/**
 * Breadcrumb store — items du fil d'Ariane affichés dans la bande shell.
 *
 * @remarks
 * État NON persisté (éphémère par construction) : la vue center courante publie
 * ses items via `useBreadcrumb([...])`, et la `BreadcrumbBar` du shell les rend.
 * Vidé au démontage de la vue.
 *
 * Vit dans `shared` (et non `app`) car consommé à la fois par le shell (`app`)
 * et par les vues (`feature`) — `feature → app` est interdit par les boundaries,
 * `feature → shared` ne l'est pas. Aucune dépendance vers `app`.
 */

import type { ReactNode } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { BreadcrumbItem } from './Breadcrumb';

interface BreadcrumbState {
  items: BreadcrumbItem[];
  /** Slot optionnel rendu au centre de la bande (ex: toggle Lecture/Édition). */
  actions: ReactNode | null;
  setItems: (items: BreadcrumbItem[]) => void;
  setActions: (actions: ReactNode | null) => void;
  clearItems: () => void;
  clearActions: () => void;
}

export const useBreadcrumbStore = create<BreadcrumbState>()(
  devtools(
    (set) => ({
      items: [],
      actions: null,
      setItems: (items) => {
        set({ items });
      },
      setActions: (actions) => {
        set({ actions });
      },
      clearItems: () => {
        set({ items: [] });
      },
      clearActions: () => {
        set({ actions: null });
      },
    }),
    { name: 'breadcrumb', enabled: import.meta.env.DEV },
  ),
);
